
import { blockCategories } from '../../categories';

export default (editor) => {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;

    const textType = domComponents.getType('text');
    const textModel = textType.model;
    const textView = textType.view;

    blockManager.add('heading', {
        label: "Überschrift",
        category: blockCategories.basic,
        content: {
            type: 'heading',
            content: 'Heading',
            classes: ['heading-h2'],
        },
        media: `<svg fill="currentColor" aria-hidden="true"  focusable="false" data-prefix="fas" data-icon="heading" class="svg-inline--fa fa-heading fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M448 96v320h32a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16H320a16 16 0 0 1-16-16v-32a16 16 0 0 1 16-16h32V288H160v128h32a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16H32a16 16 0 0 1-16-16v-32a16 16 0 0 1 16-16h32V96H32a16 16 0 0 1-16-16V48a16 16 0 0 1 16-16h160a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16h-32v128h192V96h-32a16 16 0 0 1-16-16V48a16 16 0 0 1 16-16h160a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16z"></path></svg>`,
    });

    // Define heading sizes and their corresponding classes in one place
    const headingSizes = {
        h1: { class: 'heading-h1', name: 'One (largest)' },
        h2: { class: 'heading-h2', name: 'Two' },
        h3: { class: 'heading-h3', name: 'Three' },
        h4: { class: 'heading-h4', name: 'Four' },
        h5: { class: 'heading-h5', name: 'Five' },
        h6: { class: 'heading-h6', name: 'Six (smallest)' }
    };

    const ALL_HEADING_CLASSES = Object.values(headingSizes).map(size => size.class);

    // Default heading level and class
    const defaultHeading = 'h2';
    const defaultClass = headingSizes[defaultHeading].class;

    // Generate trait options from the heading sizes
    const generateOptions = (obj) => {
        return Object.entries(obj).map(([value, props]) => ({
            value,
            name: props.name
        }));
    };

    // Generate class options from the heading sizes
    const generateClassOptions = (obj) => {
        return [
            { value: '', name: 'None' },
            ...Object.entries(obj).map(([key, props]) => ({
                value: props.class,
                name: props.name
            }))
        ];
    };

    domComponents.addType('heading', {
        extend: 'text',
        model: {
            defaults: Object.assign({}, textModel.prototype.defaults, {
                'custom-name': 'Heading',
                tagName: defaultHeading,
                droppable: false,
                attributes: { 'heading-size': defaultHeading },
                traits: [
                    {
                        type: 'select',
                        options: generateOptions(headingSizes),
                        label: 'Heading Level',
                        name: 'tagName',
                        changeProp: 1,
                        default: defaultHeading,
                    },
                    {
                        type: 'class_select',
                        options: generateClassOptions(headingSizes),
                        label: 'Display Size',
                        name: 'display_size',
                        default: defaultClass,
                    }
                ].concat(textModel.prototype.defaults.traits),
            }),

            init() {
                this.on('change:tagName', this.onTagChange);

                // Ensure the component starts with the correct class and attribute
                const tagName = this.get('tagName');
                const headingClass = headingSizes[tagName]?.class;

                // Set the heading size attribute
                this.set('attributes', {
                    ...this.get('attributes'),
                    'heading-size': tagName
                });
                const sizeClasses = ALL_HEADING_CLASSES.filter(cls =>
                    this.getClasses().includes(cls)
                );
                if (sizeClasses.length > 1) {
                    sizeClasses
                        .filter(cls => cls !== headingClass)
                        .forEach(cls => this.removeClass(cls));
                }

                if (headingClass && !this.getClasses().includes(headingClass)) {
                    this.addClass(headingClass);
                }
            },

            onTagChange() {
                const tagName = this.get('tagName');
                const newClass = headingSizes[tagName]?.class;

                // Update the heading size attribute
                this.set('attributes', {
                    ...this.get('attributes'),
                    'heading-size': tagName
                });

                // Remove all heading size classes
                Object.values(headingSizes).forEach(size => {
                    if (this.getClasses().includes(size.class)) {
                        this.removeClass(size.class);
                    }
                });

                // Add the corresponding class for the new tag
                if (newClass) {
                    this.addClass(newClass);
                }

                // Update the display size trait value
                const traits = this.get('traits');
                const displayTrait = traits.where({ name: 'display_size' })[0];
                if (displayTrait) {
                    displayTrait.set('value', newClass || '');
                }
            },


        },

        isComponent(el) {
            if (el && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(el?.tagName?.toLowerCase())) {
                return { type: 'heading' };
            }
            return undefined;
        },

        view: {
            ...textView,
            onRender() {
                if (textView.onRender) {
                    textView.onRender.call(this);
                }

                // Add any additional rendering logic here
                const model = this.model;
                const el = this.el;
                const tagName = model.get('tagName');

                // Update the heading-size attribute
                el.setAttribute('heading-size', tagName);

                // Ensure the element has the correct default class if none is set
                const hasAnySizeClass = Object.values(headingSizes).some(size =>
                    model.getClasses().includes(size.class)
                );

                if (!hasAnySizeClass && tagName === defaultHeading) {
                    model.addClass(defaultClass);
                }
            }
        }
    });
};